import type { SidebarProps } from "./types";
import { useSidebar } from "./SidebarContext";
import { SidebarWorkspace } from "./SidebarWorkspace";
import { SidebarCollapsibleSection } from "./SidebarCollapsibleSection";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarRecentAudits } from "./SidebarRecentAudits";
import { SidebarMemberList } from "./SidebarMemberList";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { useTranslation } from "@/packages/utils";

export function Sidebar({
  workspace,
  primaryNav,
  recentAudits,
  teamMembers,
  bottomNav,
  currentUser,
}: SidebarProps) {
  const { t } = useTranslation(["sidebar"]);
  const { collapsed, isMobile, toggleCollapsed } = useSidebar();

  const asideClass = isMobile
    ? `fixed inset-y-0 left-0 z-50 w-[240px] transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
        collapsed ? "-translate-x-full" : "translate-x-0"
      }`
    : `shrink-0 self-stretch relative transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
        collapsed ? "w-[60px]" : "w-[240px]"
      }`;

  return (
    <>
      <aside className={asideClass}>
        <div className="flex h-full flex-col border-r border-border/60 bg-surface">
          <SidebarWorkspace {...workspace} />

          {!collapsed && (
            <div className="mx-4 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-1.5 pt-2 scrollbar-thin">
            {/* Primary nav */}
            <div className="space-y-px">
              {primaryNav.map((item) => (
                <SidebarNavItem key={item.label} {...item} />
              ))}
            </div>

            {/* Recent Audits */}
            {recentAudits.length > 0 && (
              <SidebarCollapsibleSection
                label={t("sidebar:sections.recentAudits")}
              >
                <SidebarRecentAudits audits={recentAudits} />
              </SidebarCollapsibleSection>
            )}

            {/* Your Team */}
            {teamMembers.length > 0 && (
              <SidebarCollapsibleSection label={t("sidebar:sections.yourTeam")}>
                <SidebarMemberList members={teamMembers} />
              </SidebarCollapsibleSection>
            )}
          </div>

          {/* Bottom nav items (e.g. Admin Standards) */}
          {bottomNav.length > 0 && (
            <div className="border-t border-border/40 px-1.5 py-2">
              <div className="space-y-px">
                {bottomNav.map((item) => (
                  <SidebarNavItem key={item.label} {...item} />
                ))}
              </div>
            </div>
          )}

          {/* Bottom: User menu */}
          {currentUser && (
            <div className="border-t border-border/40 px-1.5 py-2">
              <SidebarUserMenu user={currentUser} />
            </div>
          )}
        </div>
      </aside>

      {/* Mobile overlay backdrop */}
      {isMobile && !collapsed && (
        <button
          type="button"
          aria-label={t("sidebar:actions.closeSidebar")}
          className="fixed inset-0 z-40 w-full cursor-default bg-black/40 backdrop-blur-[2px]"
          onClick={toggleCollapsed}
        />
      )}
    </>
  );
}
